import React from "react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <button onClick={() => navigate(-1)} className="text-sm text-[#4a9eff] hover:underline mb-4 inline-block" data-testid="privacy-back-btn">
            &larr; Back
          </button>
          <h1 className="text-3xl font-bold text-[#1e3a5f] tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-[#5a6577] mt-2">Last updated: March 28, 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e4e8ee] p-8 space-y-8 text-[#3d4f63] text-sm leading-relaxed">
          <p>
            Opturna ("Company," "we," "our," or "us") respects your privacy and is committed to protecting your personal information.
            This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform, website, and services (collectively, the "Service").
          </p>
          <p className="font-medium text-[#1e3a5f]">By using Opturna, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">1. Information We Collect</h2>
            <p className="mb-3">We collect information to provide better financial insights and improve our services.</p>

            <h3 className="font-semibold text-[#1e3a5f] mt-4 mb-2">A. Personal Information</h3>
            <p className="mb-1">You may provide:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Business information</li>
              <li>Account login credentials</li>
            </ul>

            <h3 className="font-semibold text-[#1e3a5f] mt-4 mb-2">B. Financial Information (User-Provided Only)</h3>
            <p className="mb-1">Opturna may collect financial inputs such as:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Revenue and income data</li>
              <li>Business expenses</li>
              <li>Entity structure</li>
              <li>General financial metrics</li>
            </ul>

            <h3 className="font-semibold text-[#1e3a5f] mt-4 mb-2">C. Financial Information (Via Plaid)</h3>
            <p className="mb-1">When you connect a financial account through Plaid, we may receive:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Account balances</li>
              <li>Transaction history</li>
              <li>Account name, type, and masked identifier</li>
              <li>Financial institution name</li>
            </ul>
            <div className="mt-3 p-3 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p className="font-semibold text-[#1e3a5f] text-xs mb-1">Important:</p>
              <p className="text-xs text-[#5a6577]">Opturna does not receive or store your online banking username, password, or security challenge responses. These credentials are handled securely by Plaid and your financial institution. You can disconnect linked accounts at any time from your account settings. For more information, see <a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline">Plaid's Privacy Policy</a>.</p>
            </div>

            <h3 className="font-semibold text-[#1e3a5f] mt-4 mb-2">C. Usage Data</h3>
            <p className="mb-1">We automatically collect:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>IP address</li>
              <li>Browser type</li>
              <li>Pages visited</li>
              <li>Time spent on platform</li>
            </ul>
            <p className="mt-1 text-xs text-[#5a6577]">This helps improve user experience.</p>

            <h3 className="font-semibold text-[#1e3a5f] mt-4 mb-2">D. Cookies & Tracking</h3>
            <p className="mb-1">We may use cookies to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Improve functionality</li>
              <li>Remember preferences</li>
              <li>Analyze platform usage</li>
            </ul>
            <p className="mt-1 text-xs text-[#5a6577]">You can disable cookies in your browser settings.</p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">2. How We Use Your Information</h2>
            <p className="mb-1">We use your information to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Provide financial insights and educational recommendations</li>
              <li>Improve platform functionality</li>
              <li>Personalize user experience</li>
              <li>Communicate updates and support</li>
              <li>Analyze trends and usage</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">3. Educational Use Disclaimer</h2>
            <p className="mb-2">Opturna provides financial and tax-related insights for educational purposes only.</p>
            <p className="mb-1 font-medium text-[#1e3a5f]">We do NOT:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Provide legal, tax, or accounting advice</li>
              <li>Guarantee tax savings</li>
              <li>Act as a licensed CPA, Enrolled Agent, or tax attorney</li>
            </ul>
            <p className="mt-2 text-[#5a6577]">Users are responsible for consulting qualified professionals before making financial decisions.</p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">4. Sharing of Information</h2>
            <p className="mb-2 font-medium text-[#1e3a5f]">We do NOT sell your personal information.</p>
            <p className="mb-1">We may share data with:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Service providers (hosting, analytics, payment processing)</li>
              <li>Legal authorities if required by law</li>
            </ul>
            <p className="mt-1 text-[#5a6577]">All third parties are required to maintain confidentiality.</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">5. Data Security</h2>
            <p className="mb-1">We take reasonable steps to protect your information using:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Secure servers</li>
              <li>Encrypted data transmission</li>
              <li>Access controls</li>
            </ul>
            <p className="mt-1 text-[#5a6577]">However, no system is 100% secure.</p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">6. Data Retention</h2>
            <p className="mb-1">We retain your information only as long as necessary to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Provide services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
            </ul>
            <p className="mt-1 text-[#5a6577]">You may request deletion of your data at any time.</p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">7. Your Rights</h2>
            <p className="mb-1">Depending on your location, you may have the right to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Access your data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion</li>
              <li>Opt out of certain data uses</li>
            </ul>
            <p className="mt-2">To exercise your rights, contact us at: <a href="mailto:Admin@opturnatech.com" className="text-[#4a9eff] hover:underline">Admin@opturnatech.com</a></p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">8. Third-Party Services</h2>
            <p>Opturna may use third-party tools for payment processing, analytics, and infrastructure. These providers have their own privacy policies.</p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">9. Children's Privacy</h2>
            <p>Opturna is not intended for users under the age of 18. We do not knowingly collect data from children.</p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy periodically. Updates will be posted with a revised "Last updated" date.</p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">11. Contact Us</h2>
            <p className="mb-2">If you have questions about this Privacy Policy, contact:</p>
            <div className="p-4 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p className="font-semibold text-[#1e3a5f]">Opturna Tech Inc.</p>
              <p className="text-[#5a6577]">8 The Green Suite 8882, Dover, DE 19901</p>
              <p><a href="mailto:Admin@opturnatech.com" className="text-[#4a9eff] hover:underline">Admin@opturnatech.com</a></p>
            </div>
          </section>
        </div>

        <p className="text-center text-xs text-[#8b95a5] mt-8">Opturna Tech Inc. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
